import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { ProjectAdapter } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { Button, Loading } from '@makyoapp/makyo-ui'

import { useGetOrgApps } from '@/resolvers/org/apps/query'
import { AppFilterProvider, useOrgAppsContext } from '@/context/OrgAppsContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { ADD_NEW_APP_ROUTE } from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import AppFilterModal from '@/components/AppFilterModal'
import Layout from '@/components/Layout'
import { PageUnauthorized } from '@/components/PageUnauthorized'

import { ProjectList } from './components/ProjectList'
import { useOrgAppAction } from './hooks/useOrgAppAction'

/**
 * main view to list app
 */
function OrgDetailAppList({ title }: WrapperProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { canAddNewApp, handleDeleteApp } = useOrgAppAction()

  // Access apps filter & search from context
  const { search, setSearch, onClearSearch, getAppFilter } = useOrgAppsContext()

  // Get App List from SDK
  const { data: apps, isLoading } = useGetOrgApps({ params: getAppFilter() })

  return (
    <Layout
      title={title}
      search={search}
      onSearch={setSearch}
      onClearSearch={onClearSearch}
      actions={
        <>
          <AppFilterModal />
          {canAddNewApp && (
            <Button
              className='cursor-pointer font-semibold'
              icon={<Icon.Plus16 />}
              onClick={() => {
                navigate(`./${ADD_NEW_APP_ROUTE}`)
              }}
            >
              {t('Add New App')}
            </Button>
          )}
        </>
      }
      wrapClassName='h-full'
    >
      {isLoading ? (
        <div className='h-full flex items-center justify-center'>
          <Loading fullHeight={false} />
        </div>
      ) : (
        <ProjectList apps={apps as ProjectAdapter[]} handleDelete={handleDeleteApp} isLoading={isLoading} />
      )}
    </Layout>
  )
}

/**
 * Org Apps Page
 *
 * @route `o/:orgId/apps`
 *
 */
export default function OrgDetailAppsPage({ title }: WrapperProps) {
  const canViewListApp = useOrgPerm(OrgPermission.AppsViewListOfApps)

  return !canViewListApp ? (
    <Layout title={title}>
      <PageUnauthorized />
    </Layout>
  ) : (
    <AppFilterProvider>
      <OrgDetailAppList title={title} />
    </AppFilterProvider>
  )
}
